import { ApolloClient } from "apollo-client";
import { getInitialCheckout as getInitialCicoCheckout } from "@comparaonline/ui-checkout-cico";
import { OfferPriceType } from "@comparaonline/cicl-funnel-schema";

import { findOrCreateOfferSale } from "./findOrCreateOfferSale";
import { getExternalURL, getQueryString, injectCurrentQueryString } from "../../../core/helpers";
import { ProductWithOffer } from "../../../core/types";
import { OfferSelectedParams } from "../../../modules/common/context/quoteFormConnector/types";
import { FirebaseClient, FunnelName } from "../../../modules/firebase";
import { Router, MapRoutes } from "../../../routes";

import { useAppContext } from "../../../lib/appContext";
import { useFirebase } from "../../../../pages/FirebaseProvider";
import { useSocketContext } from "../../../modules/common/context/quoteFormConnector";
import { getInitialCheckout as getInitialCiclCheckout } from "../../../modules/cikmcl/helpers";
import { createFunnel, hasACompletedCheckout, hasInternalCheckout } from "./helpers";
import { isInsuranceByKm } from "../isInsuranceByKm";
import { getAllowedPaymentMethods } from "../../../modules/checkout/helpers/paymentMethods";
import { getConfig } from "../../../../config/app";

export interface PaymentCTAResponse {
  params: OfferSelectedParams;
  redirectToPaymentPage: () => void;
}

type GetPaymentCta = (
  product: ProductWithOffer,
  apolloClient: ApolloClient<any>
) => {
  text: string;
  onClick: () => Promise<PaymentCTAResponse>;
};

const redirectTo = (url: string) => (params: OfferSelectedParams) => {
  window.location.href = injectCurrentQueryString(getExternalURL(url, params));
};

type OnPaymentClick = (input: {
  apolloClient: ApolloClient<any>;
  businessCode: string;
  businessUnitSlug: string;
  countryCode: string;
  firebase: FirebaseClient;
  getOfferById: (offerId: string, formId: string, callback: () => { offer: Offer }) => void;
  getQuoteFormById: (formId: string, callback: () => { form: Quote }) => void;
  product: ProductWithOffer;
  sarlaftLimits?: {
    insuranceVehicleLimit: number;
    yearlyFeeLimit: number;
  };
}) => Promise<Record<string, unknown>>;

const onPaymentClick: OnPaymentClick = ({
  apolloClient,
  businessCode,
  businessUnitSlug,
  countryCode,
  firebase,
  getOfferById,
  getQuoteFormById,
  product,
  sarlaftLimits
}) => {
  const { offer } = product;
  const params = {
    offerId: (offer && offer.id) || "",
    formId: (offer && offer.formId) || ""
  };
  const isCico = countryCode === "co";
  const funnelName = isCico ? FunnelName.Cico : FunnelName.Cicl;

  const createOfferSaleURL = getExternalURL(`${countryCode}.ci.createSale`, params);
  const getPaymentUrlParams =
    createOfferSaleURL && !hasInternalCheckout(businessCode, product)
      ? findOrCreateOfferSale(createOfferSaleURL)
      : Promise.resolve(params);

  const createCheckout = ({ quote, offer }): Promise =>
    isCico
      ? new Promise(resolve =>
          resolve(
            getInitialCicoCheckout(quote, getAllowedPaymentMethods(offer), offer, sarlaftLimits)
          )
        )
      : getInitialCiclCheckout(quote, apolloClient, offer);

  const getOfferAndQuote = (): Promise =>
    new Promise(resolve =>
      getOfferById(offer.id, offer.formId, ({ offer: fullOffer }) =>
        getQuoteFormById(offer.formId, ({ form }) => resolve({ quote: form, offer: fullOffer }))
      )
    );

  return getPaymentUrlParams.then(async response => {
    const fullData = await getOfferAndQuote();

    const redirectCallback = async () => {
      if (hasInternalCheckout(businessCode, product)) {
        const checkout = await createCheckout(fullData);
        if (!(await hasACompletedCheckout(offer.formId, firebase))) {
          await createFunnel({
            name: funnelName,
            countryCode,
            checkout,
            firebase,
            ...fullData
          });
        }

        return Router.pushRoute(MapRoutes.CHECKOUT, {
          ...getQueryString(window.location.href),
          ...params,
          business: businessUnitSlug
        });
      }
      redirectTo(`${countryCode}.ci.payment`)(response);
    };

    const { amount, total } = product.mainAttribute.value;

    return {
      params: {
        ...response,
        company: offer.company,
        formId: params.formId,
        insurerOfferId: fullData.offer.insurerOfferId,
        offerType: isInsuranceByKm(product)
          ? OfferPriceType.VARIABLE_PRICE
          : OfferPriceType.FIXED_PRICE,
        price: amount || total
      },
      redirectToPaymentPage: redirectCallback
    };
  });
};

const getPaymentCta: GetPaymentCta = (product, apolloClient) => {
  const { getOfferById, getQuoteFormById } = useSocketContext();
  const { businessUnitConfig, businessUnitSlug, countryCode } = useAppContext();
  const { quoteResultCTA, businessCode, sarlaftLimits } = businessUnitConfig;
  const firebase = useFirebase();

  return {
    onClick: () =>
      onPaymentClick({
        apolloClient,
        businessCode,
        businessUnitSlug,
        countryCode,
        firebase,
        getOfferById,
        getQuoteFormById,
        product,
        sarlaftLimits
      }),
    text: quoteResultCTA
  };
};

/* eslint-enable */

export { getPaymentCta };
